import React from 'react';

import { Box } from '@rbilabs/universal-components';
import { useIntl } from 'react-intl';

import { Calories } from 'components/price-and-calories';
import { useMenuCalories } from 'hooks/menu/use-menu-calories';
import { ProductPriceConditions, useProductPrice } from 'hooks/menu/use-product-price';
import { computeProductCalories } from 'hooks/menu/utils';
import { useProductWizardContext } from 'state/product-wizard-v2';
import { EventName, emitEvent } from 'utils/event-hub';

import { PriceAndCaloriesWrapper, PriceCaloriesText } from './styled';

interface IPriceAndCaloriesProps {
  productPriceConditions?: ProductPriceConditions;
  icon?: React.ReactNode;
}

export const PriceAndCalories: React.FC<IPriceAndCaloriesProps> = ({
  productPriceConditions,
  icon,
}) => {
  const { formatMessage } = useIntl();
  const { formattedProductPrice } = useProductPrice(productPriceConditions);

  const { selectedProduct } = useProductWizardContext();
  const { formatCalories } = useMenuCalories();

  const shouldComputeCalories = !!selectedProduct && !selectedProduct.hideCalories;
  const productCalories = shouldComputeCalories ? computeProductCalories(selectedProduct) : 0;
  const formattedProductCalories = formatCalories(productCalories);

  React.useEffect(() => {
    if (!formattedProductCalories) {
      return;
    }
    // Emit event for when calories are loaded since menu performance tracking uses this
    emitEvent(EventName.MENU_ITEM_CALORIES_LOADED);
  }, [formattedProductCalories]);

  const priceAccessibilityLabel = formatMessage(
    { id: 'totalPrice' },
    { price: formattedProductPrice }
  );

  return (
    <Box>
      <PriceAndCaloriesWrapper>
        <PriceCaloriesText
          testID="product-total-price"
          accessibilityLabel={priceAccessibilityLabel}
          focusable
          accessible
        >
          {formattedProductPrice}
        </PriceCaloriesText>

        {!!formattedProductPrice &&
          !!formattedProductCalories &&
          !selectedProduct?.hideCalories && <PriceCaloriesText>{' | '}</PriceCaloriesText>}

        <Calories hideCalories={!!selectedProduct?.hideCalories}>
          <PriceCaloriesText
            testID="product-total-calories"
            focusable
            accessible
            accessibilityLabel={formattedProductCalories}
          >
            {formattedProductCalories}
          </PriceCaloriesText>
        </Calories>
        {icon}
      </PriceAndCaloriesWrapper>
    </Box>
  );
};
